import Supercluster from "supercluster";

import { createShopClusterMarker, SHOP_MAP_CLUSTER_SELECTOR } from "./shopMapPopupCard";

// Client-side clustering for the shop maps. Overlapping shop pins collapse into a
// numbered bubble when zoomed out and separate back into individual pins as you zoom in.
// Individual (unclustered) shops are still rendered by the caller's `buildShopMarker`
// (the existing exact-anchor pins) - this module only decides cluster vs. individual and
// adds the cluster bubbles on top, recomputed on every `moveend`.
const CLUSTER_MAX_ZOOM = 16;
const CLUSTER_RADIUS = 60; // pixels

export function createShopClusterer({ map, mapboxgl, onRender } = {}) {
  let index = null;
  let buildShopMarker = null;
  let markers = [];

  function clearMarkers() {
    markers.forEach((m) => m.remove());
    markers = [];
    map.getContainer?.()?.querySelectorAll?.(SHOP_MAP_CLUSTER_SELECTOR)?.forEach((el) => el.remove());
  }

  function render() {
    if (!map || !index) return;
    clearMarkers();

    const bounds = map.getBounds?.();
    if (!bounds) return;
    const bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()];
    const zoom = Math.round(map.getZoom?.() ?? 0);

    const individualShops = [];
    index.getClusters(bbox, zoom).forEach((feature) => {
      const [lng, lat] = feature.geometry.coordinates;

      if (feature.properties.cluster) {
        const clusterId = feature.properties.cluster_id;
        const marker = createShopClusterMarker(mapboxgl, {
          count: feature.properties.point_count,
          onClick: () => {
            let targetZoom = zoom + 2;
            try {
              targetZoom = index.getClusterExpansionZoom(clusterId);
            } catch {
              /* fall back to a modest zoom-in */
            }
            map.easeTo({ center: [lng, lat], zoom: Math.min(targetZoom, CLUSTER_MAX_ZOOM + 2) });
          },
        })
          .setLngLat([lng, lat])
          .addTo(map);
        markers.push(marker);
        return;
      }

      const shop = feature.properties.shop;
      if (!shop || typeof buildShopMarker !== "function") return;
      const marker = buildShopMarker(shop);
      if (marker) markers.push(marker);
      individualShops.push(shop);
    });

    if (typeof onRender === "function") onRender(individualShops);
  }

  function setShops(shops, builder) {
    buildShopMarker = builder;
    const points = (shops || [])
      .filter((s) => Number.isFinite(s.latitude) && Number.isFinite(s.longitude))
      .map((shop) => ({
        type: "Feature",
        properties: { shop },
        geometry: { type: "Point", coordinates: [shop.longitude, shop.latitude] },
      }));
    index = new Supercluster({ radius: CLUSTER_RADIUS, maxZoom: CLUSTER_MAX_ZOOM }).load(points);
    render();
  }

  map.on?.("moveend", render);

  function destroy() {
    map.off?.("moveend", render);
    clearMarkers();
    index = null;
    buildShopMarker = null;
  }

  return { setShops, render, destroy };
}
